import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

// Which documents an asset can reach, by the same tag-scope rule the field-engineer chat uses.
// Separate from the test run because it is useful on its own: "this asset reaches nothing"
// explains an unhelpful answer before you spend a completion finding out.
export default function useFilesInScope(assetId) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // See useAdminWorkflows: a response arriving after unmount must not set state, or the test
  // runner reports an unhandled error against a torn-down environment and fails CI.
  const mountedRef = useRef(true);

  const fetchIt = useCallback(async () => {
    if (!assetId) {
      setFiles([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/ai/files_in_scope?asset_id=${assetId}`
      );
      if (!res.ok) throw new Error(res.statusText || 'Failed to load files in scope');
      const body = await res.json();
      if (!mountedRef.current) return;
      setFiles(Array.isArray(body) ? body : body.files || []);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message || 'Failed to load files in scope');
      setFiles([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchIt();
    return () => { mountedRef.current = false; };
  }, [fetchIt]);

  return { files, loading, error, refresh: fetchIt };
}
